import React from 'react';

/**
 * Curated list of common IANA time zones for the timezone dropdown used
 * throughout parent-facing setup forms. Using a dropdown (rather than a
 * free-text field) prevents parents from typing a malformed zone (e.g. a
 * typo like "America/Leangles") that Luxon accepts silently but Postgres
 * later rejects, which previously crashed a student's setup page in
 * production.
 */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain Time, no DST (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (Puerto Rico)' },
  { value: 'Europe/London', label: 'UK Time (London)' },
  { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
  { value: 'Asia/Kolkata', label: 'India Time (Kolkata)' },
  { value: 'Asia/Dubai', label: 'Gulf Time (Dubai)' },
  { value: 'Asia/Singapore', label: 'Singapore Time' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * Timezone dropdown options, guaranteed to include `current` even if it
 * isn't one of the curated common zones (e.g. a less common IANA zone
 * detected from the browser), so the <select> never silently drops the
 * existing value.
 */
export function timezoneSelectOptions(current: string): { value: string; label: string }[] {
  if (!current || TIMEZONE_OPTIONS.some((o) => o.value === current)) return TIMEZONE_OPTIONS;
  return [{ value: current, label: `${current} (detected)` }, ...TIMEZONE_OPTIONS];
}

export function Pill({ tone, children }: { tone?: 'ok' | 'warn' | 'bad'; children: React.ReactNode }) {
  return <span className={`pill${tone ? ` ${tone}` : ''}`}>{children}</span>;
}

export function statusTone(status?: string | null): 'ok' | 'warn' | 'bad' | undefined {
  switch (status) {
    case 'active':
    case 'connected':
    case 'ok':
    case 'sent':
    case 'delivered':
    case 'completed':
      return 'ok';
    case 'paused':
    case 'setup_required':
    case 'in_progress':
    case 'pending':
    case 'rate_limited':
      return 'warn';
    case 'needs_reconnection':
    case 'failed':
    case 'undelivered':
    case 'blocked':
    case 'archived':
      return 'bad';
    default:
      return undefined;
  }
}

export function Loading({ what }: { what: string }) {
  return <div className="card" role="status" aria-live="polite">Loading {what}…</div>;
}

export function ErrorBanner({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="card banner bad" role="alert">
      <strong>Something went wrong.</strong>
      <div className="muted">{error}</div>
      {onRetry && <p><button onClick={onRetry}>Try again</button></p>}
    </div>
  );
}

export function Empty({
  children, action, illustration,
}: { children: React.ReactNode; action?: React.ReactNode; illustration?: React.ReactNode }) {
  const body = (
    <div className="empty-state">
      <p className="empty">{children}</p>
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
  if (!illustration) return body;
  return (
    <div className="empty-illustrated">
      <div className="illustration-wrap">{illustration}</div>
      {body}
    </div>
  );
}

/** Warm greeting banner used at the top of the Family Command Center and Open Student Assistant. */
export function Hero({
  eyebrow, title, subtitle, illustration, stats,
}: {
  eyebrow?: string; title: React.ReactNode; subtitle?: React.ReactNode;
  illustration: React.ReactNode; stats?: React.ReactNode[];
}) {
  return (
    <section className="hero">
      <div className="illustration-wrap hero-illustration">{illustration}</div>
      <div className="hero-copy">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
        {stats && stats.length > 0 && (
          <div className="hero-stats">
            {stats.map((s, i) => <span key={i} className="hero-stat">{s}</span>)}
          </div>
        )}
      </div>
    </section>
  );
}

/** A short, reassuring band explaining who can see what and how to opt out. Never used for enforcement — just clarity. */
export function TrustStrip({ illustration, children }: { illustration: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="trust-strip" role="note">
      <div className="illustration-wrap">{illustration}</div>
      <div>{children}</div>
    </div>
  );
}

/** Segmented pill navigation used to switch between sections without a long single-page scroll. */
export function SegmentedTabs({
  tabs, active, onChange,
}: { tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void }) {
  return (
    <nav className="tabs-segmented" aria-label="Assistant sections">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          aria-current={active === t.id ? 'true' : undefined}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

/** Horizontal step tracker used across the parent onboarding journey. */
export function StepProgress({ steps, currentIndex }: { steps: string[]; currentIndex: number }) {
  return (
    <ol className="step-progress" aria-label="Setup progress">
      {steps.map((label, i) => {
        const state = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
        return (
          <li key={label} className={`step-progress-item ${state}`} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="step-progress-marker">{state === 'done' ? '✓' : i + 1}</span>
            <span className="step-progress-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** Realistic sample text message so parents/students know what a reminder looks like before consenting. */
export function SmsPreview({ sample, caption }: { sample?: string; caption?: string }) {
  const text = sample ?? 'Hi Alex! Your Chemistry lab report is due tomorrow at 8:00 AM. Reply DONE when finished or SNOOZE for a later reminder. Reply HELP for help or STOP to unsubscribe.';
  return (
    <div className="sms-preview">
      <div className="sms-preview-caption">{caption ?? 'What a reminder text looks like'}</div>
      <div className="sms-preview-phone">
        <div className="sms-preview-bubble">{text}</div>
      </div>
    </div>
  );
}

/** Small controlled form helper so every view gets labels and keyboard support for free. */
export function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  inputRef?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  helperText?: React.ReactNode;
  error?: string;
}) {
  const id = React.useId();
  const describedBy = [
    props.helperText ? `${id}-help` : null,
    props.error ? `${id}-error` : null,
  ].filter(Boolean).join(' ') || undefined;
  return (
    <div>
      <label htmlFor={id}>{props.label}</label>
      {props.options ? (
        <select id={id} value={props.value} disabled={props.disabled} onChange={(e) => props.onChange(e.target.value)}>
          {props.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          id={id}
          ref={props.inputRef}
          autoFocus={props.autoFocus}
          type={props.type ?? 'text'}
          value={props.value}
          disabled={props.disabled}
          readOnly={props.readOnly}
          required={props.required}
          placeholder={props.placeholder}
          aria-describedby={describedBy}
          aria-invalid={props.error ? true : undefined}
          onChange={(e) => props.onChange(e.target.value)}
        />
      )}
      {props.helperText && <p id={`${id}-help`} className="field-help">{props.helperText}</p>}
      {props.error && <p id={`${id}-error`} className="field-help field-error" role="alert">{props.error}</p>}
    </div>
  );
}

export function useAsync<T>(fn: () => Promise<T>, deps: React.DependencyList) {
  const [state, setState] = React.useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  const [nonce, setNonce] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fn()
      .then((data) => { if (alive) setState({ data, loading: false }); })
      .catch((e) => { if (alive) setState({ error: e.message ?? String(e), loading: false }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}
